/**
 * The paper and font menu.
 *
 * Here one only chooses, nothing is applied: the component writes signals,
 * and an effect elsewhere sets the CSS variables from them and loads the
 * font. Before, every click called `applyPaper()` and `save()` itself — and
 * whoever forgot one place got a sheet that did not match the selection.
 */

import { signal } from '@preact/signals'
import type { RefObject } from 'preact'
import { useRef } from 'preact/hooks'
import * as st from '../state.js'
import * as store from '../editor/store.js'
import type { BuiltinFont, Font, Paper, PaperId } from '../types.js'
import { Icon } from './icons.js'
import { Panel, Toggle } from './Panel.js'
import { BUILTIN_FONTS, DEFAULT_FONT, PAPERS } from './paper.js'

/**
 * Is a file hovering over the menu right now?
 *
 * Outside the component, so that the state does not survive a closing and
 * opening of the menu — and so that no render resets it while someone is
 * still hovering over the area.
 */
const over = signal(false)

export function PaperPanel({ anchor }: { anchor: RefObject<HTMLButtonElement> }) {
  const open = st.panel.value === 'paper'
  const file = useRef<HTMLInputElement>(null)

  /*
   * The free row exists only for squared paper.
   *
   * On ruled paper it would make no sense: there the line spacing is already
   * chosen so that ascenders and descenders fit in. On squared paper a square
   * is often too narrow for that — there one writes in every second row.
   */
  const paper: Paper | undefined = PAPERS[st.paper.value]
  const grid = paper?.kind === 'grid'

  return (
    <Panel
      open={open}
      title="Papier"
      anchor={anchor}
      onClose={() => (st.panel.value = null)}
      note="Normalerweise wird nur der Text gedruckt — für Heftpapier, das seine eigenen Linien hat."
    >
      <div class="paper-choices" role="radiogroup" aria-label="Untergrund">
        {Object.entries(PAPERS).map(([id, p]) => (
          <PaperChoice key={id} id={id} paper={p} />
        ))}
      </div>

      {/* The box `grid-spacing-row` is gone: it was only a marker for
          hiding. What does not apply is now not drawn at all. */}
      {grid && (
        <Toggle
          checked={st.gridSpacing.value}
          onChange={(v) => (st.gridSpacing.value = v)}
        >
          Eine Reihe frei lassen
        </Toggle>
      )}

      <Toggle checked={st.printBg.value} onChange={(v) => (st.printBg.value = v)}>
        Linien mitdrucken
      </Toggle>

      {/*
       * Drag and drop hangs on this one box, not additionally on the drop area
       * inside it: events bubble up, and a drop on the area would otherwise
       * hit both handlers — the font would land in the database twice.
       */}
      <div
        class="paper-section"
        onDragOver={(e) => {
          if (!e.dataTransfer?.types?.includes('Files')) return
          e.preventDefault()
          over.value = true
        }}
        onDragLeave={(e) => {
          // Only when leaving the box itself, not when moving between its
          // children.
          const to = e.relatedTarget as Node | null
          if (!to || !e.currentTarget.contains(to)) over.value = false
        }}
        onDrop={(e) => {
          e.preventDefault()
          over.value = false
          void acceptFont(e.dataTransfer?.files?.[0])
        }}
      >
        <span class="paper-section-title">Schrift</span>

        <ul class="font-list">
          {BUILTIN_FONTS.map((f) => (
            <FontItem key={f.id} font={f} />
          ))}
          {st.fonts.value.map((f) => (
            <FontItem key={f.id} font={f} />
          ))}
        </ul>

        {/* The drop area is a button at the same time: whoever does not want
            to drag, or sits at a tablet, taps on it and gets the file
            dialog. */}
        <button
          type="button"
          class={`font-drop${over.value ? ' over' : ''}`}
          onClick={() => file.current?.click()}
        >
          <Icon name="type" />
          <span>
            Eigene Schrift hierher ziehen
            <br />
            <small>oder klicken · WOFF2, WOFF, TTF, OTF</small>
          </span>
        </button>

        <input
          ref={file}
          type="file"
          accept=".woff2,.woff,.ttf,.otf,font/*"
          hidden
          onChange={(e) => {
            const input = e.currentTarget as HTMLInputElement
            void acceptFont(input.files?.[0]).then(() => {
              input.value = ''
            })
          }}
        />
      </div>
    </Panel>
  )
}

/** One paper to choose. The preview shows the background, not a symbol. */
function PaperChoice({ id, paper }: { id: PaperId; paper: Paper }) {
  const current = id === st.paper.value
  return (
    <button
      type="button"
      class={`paper-choice${current ? ' current' : ''}`}
      role="radio"
      aria-checked={current}
      onClick={() => (st.paper.value = id)}
    >
      <span class={`paper-preview paper-preview-${paper.kind}`} aria-hidden="true" />
      <span class="paper-label">{paper.label}</span>
      <span class="paper-hint">{paper.hint}</span>
    </button>
  )
}

/**
 * One row of the font list.
 *
 * The built-in font always stands at the top and cannot be deleted — it is
 * the way back if an added handwriting is unreadable.
 */
function FontItem({ font }: { font: Font | BuiltinFont }) {
  const builtin = 'builtin' in font && font.builtin === true
  const current = st.fontId.value === font.id

  return (
    <li class={`font-item${current ? ' current' : ''}`}>
      <button
        type="button"
        class="font-pick"
        onClick={() => (st.fontId.value = font.id)}
      >
        {/* The name in the font itself: this way one sees at once what to
            expect, without having to select it first. That holds for the
            built-in handwriting too — only Atkinson stands in the interface
            font, because here it IS the interface font. */}
        <span
          class="font-name"
          style={font.id === 'builtin' ? undefined : { fontFamily: font.family }}
        >
          {font.name.replace(/\.[^.]+$/, '')}
        </span>
        {builtin && <span class="font-tag">eingebaut</span>}
      </button>

      {!builtin && (
        <button
          type="button"
          class="docs-remove"
          title="Schrift entfernen"
          aria-label={`„${font.name}“ entfernen`}
          onClick={(e) => {
            e.stopPropagation()
            void removeFont(font.id)
          }}
        >
          <Icon name="x" size={14} />
        </button>
      )}
    </li>
  )
}

async function removeFont(id: string): Promise<void> {
  await store.deleteFont(id)
  st.fonts.value = await store.fonts()
  // If it was the font in use, back to the default.
  if (st.fontId.value === id) st.fontId.value = DEFAULT_FONT.id
}

/**
 * Accepts a font file.
 *
 * The extension is checked, not the reported type: for .otf and .ttf browsers
 * report different things depending on the system — sometimes `font/otf`,
 * sometimes `application/octet-stream`, sometimes nothing at all.
 */
async function acceptFont(file: File | null | undefined): Promise<void> {
  if (!file) return
  if (!store.isFontFile(file.name)) {
    st.toast.value = 'Das ist keine Schriftdatei (WOFF2, WOFF, TTF oder OTF).'
    return
  }
  try {
    const f = await store.addFont(file)
    st.fonts.value = await store.fonts()
    st.fontId.value = f.id
    st.toast.value = `„${f.name}“ ist jetzt deine Schrift.`
  } catch (err) {
    console.error(err)
    st.toast.value = 'Die Schrift lässt sich nicht speichern.'
  }
}
